import React, { useEffect, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";

import { faCheck, faQuestionCircle, faSpinner, faWarning, faClipboardQuestion, faRotate } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  TaskPromptProps,
  ValidationConstraints,
} from "@/components/tasks/types";
import { apiClient } from "@/api";
import toast from "@/utils/toast";
import { sanitizeName } from "@/utils/strings";
import { PROMPT_COMPONENTS } from "./elements";
import PromptHeader from "./elements/shared/PromptHeader";

import AppButton from "@/components/structure/AppButton";
import AlertBox from "@/components/structure/AlertBox";
import { TaskPrompt as TaskPromptType, TaskPromptResponse } from "@/api/types";

// Component
export default function TaskPrompt({
  prompt,
  response = null,
  isReviewing = false,
  readOnly = false,
  showType = false,
  showValue = true,
  richTextDescription = true,
  fieldName,
  onPromptResponseChange = () => {},
  onOpenInterviewSidebar = (prompt: TaskPromptType) => {},
}: TaskPromptProps) {
  const formContext = useFormContext();

  const resolvedFieldName = fieldName || (prompt?.name ? sanitizeName(`prompt_${prompt.name}`) : "");

  const [retryingReview, setRetyingReview] = useState(false);
  const [runningInterview, setRunningInterview] = useState(false);

  // we use currentResponse to keep track of the response in state, so that when
  // we update it after retrying review, it triggers a re-render with the
  // updated response data (including updated review results and justifications)
  const [currentResponse, setCurrentResponse] = useState(response);

  const { name, header, description, type, options } = prompt || {};
  const hideReviewActions =
    currentResponse?.isRelevant === true &&
    currentResponse?.isClearDetailed === true;

  const isTextPromptType =
    type === "single-line-textbox" || type === "multi-line-textbox";

  const readOnlyValue = prompt?.value;
  const responseText = currentResponse?.responseText?.trim();

  const parseReviewResponseValue = () => {
    if (!currentResponse?.responseText) return undefined;

    const rawResponse = currentResponse.responseText;
    const trimmedResponse = rawResponse.trim();

    if (type === "multiple-choice" && (options as any)?.allowMultipleSelections) {
      if (!trimmedResponse) return [];

      return trimmedResponse
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
    }

    if (type === "checkbox") {
      const normalized = trimmedResponse.toLowerCase();

      if (["true", "1", "yes", "y", "checked", "on"].includes(normalized)) {
        return true;
      }

      if (["false", "0", "no", "n", "unchecked", "off", ""].includes(normalized)) {
        return false;
      }

      return Boolean(trimmedResponse);
    }

    return rawResponse;
  };

  useEffect(() => {
    if (!resolvedFieldName) return;

    if (isReviewing) {
      const reviewValue = parseReviewResponseValue();
      if (reviewValue !== undefined) {
        formContext.setValue(resolvedFieldName, reviewValue, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
        return;
      }
    }

    if (!isReviewing) return;

    const currentValue = formContext.getValues(resolvedFieldName);
    const hasCurrentValue =
      currentValue !== undefined &&
      currentValue !== null &&
      !(typeof currentValue === "string" && currentValue.trim() === "");

    if (!hasCurrentValue && prompt?.value !== undefined) {
      formContext.setValue(resolvedFieldName, prompt.value, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [
    currentResponse?.responseText,
    formContext,
    isReviewing,
    options,
    prompt?.value,
    resolvedFieldName,
    type,
  ]);

  // Extract validation constraints from options
  const getValidationConstraints = (): ValidationConstraints | undefined => {
    const typedOptions = options as any;
    return typedOptions?.validation || {};
  };

  const isRequired = getValidationConstraints()?.required || false;

  // Convert validation constraints to react-hook-form rules
  const getValidationRules = () => {
    const constraints = getValidationConstraints();
    if (!constraints) return {};

    const rules: any = {};

    if (constraints.required) {
      if (type === 'checkbox') {
        rules.required = {
          value: true,
          message: constraints.customMessage || `${name} must be checked`
        };
      } else {
        rules.required = {
          value: true,
          message: constraints.customMessage || `${name} is required`
        };
      }
    }

    if (constraints.minLength) {
      rules.minLength = {
        value: constraints.minLength,
        message: `${name} must be at least ${constraints.minLength} characters`
      };
    }

    if (constraints.maxLength) {
      rules.maxLength = {
        value: constraints.maxLength,
        message: `${name} must be no more than ${constraints.maxLength} characters`
      };
    }

    if (constraints.pattern) {
      rules.pattern = {
        value: new RegExp(constraints.pattern),
        message: constraints.customMessage || `${name} format is invalid`
      };
    }

    if (constraints.min !== undefined) {
      rules.min = {
        value: constraints.min,
        message: `${name} must be at least ${constraints.min}`
      };
    }

    if (constraints.max !== undefined) {
      rules.max = {
        value: constraints.max,
        message: `${name} must be no more than ${constraints.max}`
      };
    }

    return rules;
  };

  const renderPromptByType = () => {
    // if we're readOnly, show the value
    if (readOnly) {
      return (
        <div className="p-3 border border-gray-200 rounded bg-gray-50 min-h-[38px]">
          <div>
            {readOnlyValue || (
              <span className="text-gray-400">No response</span>
            )}
          </div>
          {!isTextPromptType && responseText && responseText !== readOnlyValue && (
            <div className="mt-1 text-sm italic text-gray-500">
              {responseText}
            </div>
          )}
        </div>
      );
    }

    const Component = PROMPT_COMPONENTS[type];

    if (!Component) {
      return (
        <div className="text-red-500">Unsupported prompt type: {type}</div>
      );
    }

    // TBC: when in review, i want this to be set by default to prompt?.value
    return (
      <div className="space-y-2">
        <Controller
          name={resolvedFieldName}
          control={formContext.control}
          rules={getValidationRules()}
          render={({ field, fieldState: { error } }) => (
            <Component
              name={name}
              options={options as any}
              field={field}
              error={error}
              readOnly={readOnly}
            />
          )}
        />
        {isReviewing && responseText && (
          <div className="px-1 text-sm italic text-gray-500">
            <b>Full Answer:</b> {responseText}
          </div>
        )}
      </div>
    );
  };

  const retryReview = () => {
    // we'll be making a PATCH request to /api/task-responses/{response.id}/retry-review/?task_prompt_pk=<> with the contents of
    // the prompt supplied as new_response.
    // URL for reference `/api/task-responses/${response.id}/retry-review/?task_prompt_pk=${prompt.id}`
    // we should use apiClient for this, though
    setRetyingReview(true);
    console.log(response);
    apiClient.patch<TaskPromptResponse>(`/task-responses/${currentResponse?.taskResponse}/retry-response-review/?task_prompt_pk=${currentResponse?.id}`,
      {
        new_response: formContext.getValues(resolvedFieldName),
      }
    ).then((res) => {
      // on success, we can show a success message or update the UI as needed
      toast.info("Review retried successfully!");
      console.log("Got updated prompt response data from server after retrying review:", res);
      setCurrentResponse(res); // update currentResponse with the updated data from the server
      onPromptResponseChange(res); // notify parent component of the updated response
    }).catch((err) => {
      // on error, we can show an error message
      toast.error("Error retrying review: " + err.message);
    }).finally(() => {
      setRetyingReview(false);
    });
  };

  // conversation breaks are a special case - they don't have a label/description
  // and just render as a horizontal rule
  if (type === "convo-break") {
    return <hr className="my-6 border-t border-gray-300" />;
  }

  return (
    <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
      <PromptHeader
        name={name}
        header={header}
        description={description}
        isRequired={isRequired}
        richTextDescription={richTextDescription}
      />

      {isReviewing && prompt.isReviewable && currentResponse && (
        !retryingReview
          ? (
            <div className="">
              <div>
                {currentResponse?.isRelevant ? (
                    <AlertBox type="success" headerText="Relevant Response">
                      Your response was marked as relevant.
                    </AlertBox>
                  ) : (
                    <AlertBox type="warning" headerText="Irrelevant Response">
                      Your response was marked as irrelevant.

                      {currentResponse.relevanceResults && (
                        <div>
                          <div className="font-bold mt-2">Justification:</div>
                          {currentResponse.relevanceResults.justification}
                        </div>
                      )}
                    </AlertBox>
                  )}
              </div>

              <div>
                {currentResponse?.isClearDetailed ? (
                    <AlertBox type="success" headerText="Clear Response">
                      Your response was marked as clear and detailed.
                    </AlertBox>
                  ) : (
                    <AlertBox type="warning" icon={faQuestionCircle} headerText="Vague Response">
                      Your response was marked as vague or lacking detail.

                      {currentResponse.vaguenessResults && (
                        <div>
                          <div className="font-bold mt-2">Justification:</div>
                          {currentResponse.vaguenessResults.justification}
                        </div>
                      )}
                    </AlertBox>
                  )}
              </div>
            </div>
        ) : (
          /* show a loading state with spinner while retrying review */
          <div className="p-3 mb-3 border border-gray-200 rounded bg-gray-50 flex items-center space-x-2">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2 text-gray-500" />
            <span className="text-gray-500">Retrying review...</span>
          </div>
        )
      )}

      {showValue && <div className="space-y-3">{renderPromptByType()}</div>}

      {/* Type indicator for development/debugging */}
      {showType && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
            Type: {type}
          </span>
        </div>
      )}

      {/* If reviewing, show a button to resubmit this prompt's response for review */}
      {isReviewing && prompt?.isReviewable && currentResponse && !hideReviewActions && (
        <div className="mt-4">
          <AppButton
            variant="primary"
            disabled={retryingReview}
            onClick={() => {
              // call retry review API endpoint for this prompt response
              retryReview();
            }}
          >
            <FontAwesomeIcon icon={faRotate} className="mr-2" />
            Resubmit for Review
          </AppButton>

          <AppButton
            variant="primary"
            className="ml-2"
            disabled={runningInterview || retryingReview}
            onClick={() => {
              // show the interview sidebar for this prompt
              console.log("Opening interview sidebar for prompt:", prompt);
              onOpenInterviewSidebar(prompt);
            }}
          >
            <FontAwesomeIcon icon={faClipboardQuestion} className="mr-2" />
            Start Interview
          </AppButton>
        </div>
      )}
    </div>
  );
}
